import { lazy, Suspense, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { api, unwrap, type StatusOut } from "../../api/client";
import { useAttribution } from "../../components/Attribution";
import { ExportButtons } from "../../components/ExportButtons";
import { ErrorBoundary } from "../../components/ErrorBoundary";
import { ErrorMessage, Loading, SeverityBadge, StatusBadge } from "../../components/ui";
import { t, type MessageKey } from "../../i18n/t";
import { confidenceLabel } from "../../lib/confidence";
import { isClosedStatus } from "../../lib/format";
import { routes } from "../../lib/routes";
import { usePageTitle } from "../../lib/usePageTitle";
import { useQuery } from "../../lib/useQuery";
import { findingSummaries, schoolInfo, withFindingStatus } from "../../map/geo";
import {
  COLORS,
  DEFAULT_LAYERS,
  SEVERITY_COLORS,
  type LayerGroup,
  type LayerVisibility,
} from "../../map/style";
import { FindingPanel } from "./FindingPanel";
import { useKommune } from "./KommuneContext";

const SchoolMapView = lazy(() => import("../../map/SchoolMapView"));

const LAYER_LABELS: [LayerGroup, MessageKey][] = [
  ["catchment", "map.layerCatchment"],
  ["routes", "map.layerRoutes"],
  ["findings", "map.layerFindings"],
  ["crossings", "map.layerCrossings"],
  ["accidents", "map.layerAccidents"],
  ["fau", "map.layerFau"],
];

function Swatch({
  color,
  shape,
  border,
}: {
  color: string;
  shape: "dot" | "line";
  border?: string;
}) {
  return (
    <span
      className={`swatch swatch-${shape}`}
      style={{ background: color, borderColor: border ?? color }}
      aria-hidden="true"
    />
  );
}

function Legend({ layers }: { layers: LayerVisibility }) {
  return (
    <details className="legend" open>
      <summary>{t("map.legend")}</summary>
      <ul className="plain-list legend-list">
        <li>
          <Swatch color={COLORS.school} shape="dot" border="#fff" /> {t("map.legendSchool")}
        </li>
        <li>
          <Swatch color={COLORS.route} shape="line" /> {t("map.legendRoutes")}
        </li>
        <li>
          <Swatch color={SEVERITY_COLORS.high} shape="dot" /> {t("severity.high")}
          <Swatch color={SEVERITY_COLORS.medium} shape="dot" /> {t("severity.medium")}
          <Swatch color={SEVERITY_COLORS.low} shape="dot" /> {t("severity.low")}
        </li>
        <li>
          <span className="swatch swatch-dot dimmed" aria-hidden="true" /> {t("map.legendDimmed")}
        </li>
        <li>
          <Swatch color={COLORS.crossingWith} shape="dot" border="#fff" />{" "}
          {t("map.legendCrossingWith")}
        </li>
        <li>
          <Swatch color={COLORS.crossingWithout} shape="dot" border={COLORS.crossingStroke} />{" "}
          {t("map.legendCrossingWithout")}
        </li>
        <li>
          <Swatch color={COLORS.fau} shape="dot" border="#fff" /> {t("map.legendFau")}
        </li>
        {layers.accidents && (
          <>
            <li>
              <Swatch color={COLORS.accident} shape="dot" border="#fff" /> {t("map.legendAccident")}
            </li>
            <li>
              <Swatch color="rgba(108, 52, 131, 0.25)" shape="dot" border={COLORS.accident} />{" "}
              {t("map.legendCluster")}
            </li>
          </>
        )}
      </ul>
      {layers.accidents && <p className="muted small">{t("map.accidentsNote")}</p>}
    </details>
  );
}

export function SchoolMapPage() {
  const { orgId, nr, navn, reloadOverview } = useKommune();
  const { schoolId = "" } = useParams();
  const [params, setParams] = useSearchParams();
  const selectedKey = params.get("funn");
  const [layers, setLayers] = useState<LayerVisibility>(DEFAULT_LAYERS);

  const mapQuery = useQuery(`map:${orgId}:${nr}:${schoolId}`, (signal) =>
    unwrap(
      api.GET("/api/orgs/{org_id}/kommuner/{nummer}/schools/{school_id}/map", {
        params: { path: { org_id: orgId, nummer: nr, school_id: schoolId } },
        signal,
      }),
    ),
  );
  const data = mapQuery.data;
  const school = data ? schoolInfo(data) : null;
  const findings = useMemo(() => (data ? findingSummaries(data) : []), [data]);
  useAttribution(data?.attribution);
  usePageTitle(school?.name || navn);

  function select(key: string | null) {
    const next = new URLSearchParams(params);
    if (key) next.set("funn", key);
    else next.delete("funn");
    setParams(next);
  }

  function onSaved(out: StatusOut) {
    mapQuery.setData((prev) => prev && withFindingStatus(prev, out.key, out.status));
    reloadOverview();
  }

  if (mapQuery.error) {
    return (
      <div className="page">
        <p>
          <Link to={routes.dashboard(orgId, nr)}>{t("map.back")}</Link>
        </p>
        <ErrorMessage error={mapQuery.error} onRetry={mapQuery.reload} />
      </div>
    );
  }
  if (!data || !school) return <Loading />;

  const mapLabel = t("map.mapLabel", { school: school.name });

  return (
    <div className="page page-wide">
      <header className="page-header">
        <p className="breadcrumbs">
          <Link to={routes.dashboard(orgId, nr)}>{t("map.back")}</Link>
          <Link to={routes.tiltak(orgId, nr, { skole: schoolId })}>{t("map.tiltakLink")}</Link>
        </p>
        <h1>{school.name}</h1>
        {school.trinn && <p className="muted">{t("map.trinn", { trinn: school.trinn })}</p>}
        <ExportButtons orgId={orgId} nr={nr} schoolId={schoolId} schoolName={school.name} />
      </header>

      <div className="map-layout">
        <div className="map-main">
          <fieldset className="layer-toggles">
            <legend>{t("map.layers")}</legend>
            {LAYER_LABELS.map(([group, label]) => (
              <label key={group} className="checkbox">
                <input
                  type="checkbox"
                  checked={layers[group]}
                  onChange={(e) => setLayers((l) => ({ ...l, [group]: e.target.checked }))}
                />
                {t(label)}
              </label>
            ))}
          </fieldset>
          <ErrorBoundary
            fallback={
              <div className="map-canvas map-failed" role="note">
                <p>{t("map.mapUnavailable")}</p>
              </div>
            }
          >
            <Suspense
              fallback={
                <div className="map-canvas">
                  <Loading label={t("map.loadingMap")} />
                </div>
              }
            >
              <SchoolMapView
                data={data}
                layers={layers}
                selectedKey={selectedKey}
                onSelectFinding={select}
                label={mapLabel}
              />
            </Suspense>
          </ErrorBoundary>
          <Legend layers={layers} />
        </div>

        <aside className="map-side">
          {selectedKey ? (
            <FindingPanel
              key={selectedKey}
              orgId={orgId}
              nr={nr}
              findingKey={selectedKey}
              onClose={() => select(null)}
              onSaved={onSaved}
            />
          ) : (
            <section aria-labelledby="school-findings">
              <h2 id="school-findings">{t("map.findingsList", { count: findings.length })}</h2>
              {findings.length === 0 ? (
                <p>{t("map.noFindings")}</p>
              ) : (
                <>
                  <p className="muted small">{t("map.selectHint")}</p>
                  <ol className="finding-list">
                    {findings.map((f) => (
                      <li key={f.key} className={isClosedStatus(f.status) ? "closed" : undefined}>
                        <button
                          type="button"
                          className="finding-button"
                          onClick={() => select(f.key)}
                        >
                          <span className="finding-rank">{f.rank}</span>
                          <span className="finding-text">
                            <span className="finding-title">{f.title}</span>
                            <span className="finding-location">{f.location}</span>
                          </span>
                          <span className="finding-badges">
                            <SeverityBadge severity={f.severity} />
                            {(f.confidence ?? "robust") !== "robust" && (
                              <span className="badge" title={t("finding.confidence")}>
                                {confidenceLabel(f.confidence ?? "robust")}
                              </span>
                            )}
                            <StatusBadge status={f.status} />
                          </span>
                        </button>
                      </li>
                    ))}
                  </ol>
                </>
              )}
            </section>
          )}
        </aside>
      </div>
    </div>
  );
}
